import React, { useEffect, useRef } from 'react';

const OTP_LENGTH = 6;

const OtpDigitInputs = ({
  value = '',
  onChange,
  disabled = false,
  error = false,
  autoFocus = true,
  idPrefix = 'otp',
}) => {
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => (value || '')[i] || '');
  const refs = useRef([]);

  useEffect(() => {
    if (!autoFocus || disabled) return undefined;
    const firstEmpty = digits.findIndex((d) => !d);
    const index = firstEmpty === -1 ? OTP_LENGTH - 1 : firstEmpty;
    refs.current[index]?.focus();
    return undefined;
  }, [autoFocus, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (nextDigits) => {
    onChange?.(nextDigits.join('').slice(0, OTP_LENGTH));
  };

  const handleChange = (index, raw) => {
    const cleaned = String(raw || '').replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digits];
      next[index] = '';
      commit(next);
      return;
    }
    if (cleaned.length > 1) {
      const next = [...digits];
      cleaned.slice(0, OTP_LENGTH - index).split('').forEach((ch, offset) => {
        next[index + offset] = ch;
      });
      commit(next);
      const focusAt = Math.min(index + cleaned.length, OTP_LENGTH - 1);
      refs.current[focusAt]?.focus();
      return;
    }
    const next = [...digits];
    next[index] = cleaned;
    commit(next);
    if (index < OTP_LENGTH - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      commit(next);
      refs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] || '');
    commit(next);
    refs.current[Math.min(pasted.length, OTP_LENGTH) - 1]?.focus();
  };

  return (
    <div className="flex items-center justify-between gap-1.5 sm:gap-2" onPaste={handlePaste}>
      {digits.map((digit, index) => (
        <input
          key={`${idPrefix}-${index}`}
          ref={(el) => { refs.current[index] = el; }}
          id={`${idPrefix}-${index}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          value={digit}
          aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          className={`h-12 w-10 sm:h-14 sm:w-12 rounded-xl border text-center text-lg sm:text-xl font-semibold tabular-nums text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-60 ${
            error
              ? 'border-red-500'
              : digit
                ? 'border-primary-400 dark:border-primary-500'
                : 'border-gray-300 dark:border-gray-600'
          }`}
        />
      ))}
    </div>
  );
};

export default OtpDigitInputs;
export { OTP_LENGTH };
